import { useState } from 'react';
import { Plus } from 'lucide-react';
import {
  Button,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@vnetwork/ui/primitives';

export interface PickerOption {
  value: string;
  label: string;
}

/**
 * Inline "pick one, then add" control reused by the policy-attach and
 * group-member flows. Holds its own draft selection; on Add it fires `onAdd`
 * with the chosen value and clears. Disabled (with a hint) when nothing is
 * left to pick so the operator sees why.
 */
export function AttachPicker({
  options,
  onAdd,
  pending,
  placeholder,
  addLabel,
  emptyHint,
}: {
  options: PickerOption[];
  onAdd: (value: string) => void;
  pending?: boolean;
  placeholder: string;
  addLabel: string;
  emptyHint: string;
}) {
  const [value, setValue] = useState('');
  const noOptions = options.length === 0;

  const add = () => {
    if (!value) return;
    onAdd(value);
    setValue('');
  };

  if (noOptions) {
    return (
      <p style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)', margin: 0 }}>
        {emptyHint}
      </p>
    );
  }

  return (
    <div style={{ display: 'flex', gap: 'var(--vn-space-2)', alignItems: 'center' }}>
      <div style={{ minWidth: 280 }}>
        <Select value={value} onValueChange={setValue}>
          <SelectTrigger>
            <SelectValue placeholder={placeholder} />
          </SelectTrigger>
          <SelectContent>
            {options.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <Button
        variant="secondary"
        startIcon={<Plus size={14} />}
        disabled={!value || pending}
        onClick={add}
      >
        {addLabel}
      </Button>
    </div>
  );
}
